import React, { useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DownloadIcon from '@mui/icons-material/Download';
import SendIcon from '@mui/icons-material/Send';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useRouter } from '../../hooks/use-router';
import PAGE_ROUTES, { API_ROUTES } from '../../utils/constant';
import { getCurrentAuth } from '../../hooks/http-server';
import { toast } from 'react-toastify';
import HttpService from '../../hooks/Https-services';
import { useParams } from 'react-router-dom';

const baseUrl = process.env.REACT_APP_BASE_URL;
const OliveHeader = '#7b6914';
const LightYellow = '#fef9c3';

interface LineItem {
  id: number;
  description: string;
  team_label: string;
  cost_per: string;
  quantity: string;
  amount: string;
}
interface GameRecord {
  game_id: number;
}
interface InvoiceData {
  id: number;
  invoice_number: number;
  billing_period_start: string;
  billing_period_end: string;
  subtotal: string;
  total: string;
  status: 'DRAFT' | 'SENT';
  generated_at: string;
  sent_at: string | null;
  client: {
    company_name: string;
    address_line1: string;
    address_line2?: string;
    city_state_zip: string;
    customer_id: string;
    terms: string;
  };
  lineItems: LineItem[];
  gameRecords: GameRecord[];
}

const InvoiceDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const router = useRouter();
  const [invoice, setInvoice] = useState<InvoiceData | null>(null);
  const [loading, setLoading] = useState(true);
  const [sendingEmail, setSendingEmail] = useState(false);

  useEffect(() => {
    HttpService.get(API_ROUTES.invoiceById.replace(':id', id!))
      .then((res) => setInvoice(res.data.data))
      .catch(() => toast.error('Failed to fetch invoice'))
      .finally(() => setLoading(false));
  }, [id]);

  const handleDownloadPdf = () => {
    const token = getCurrentAuth()?.Token;
    if (!token) {
      toast.error('Not authenticated');
      return;
    }
    const url = `${baseUrl}${API_ROUTES.invoicePdf.replace(':id', id!)}`;
    fetch(url, { headers: { Authorization: `bearer ${token}` } })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.blob();
      })
      .then((blob) => {
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = `invoice-${invoice?.invoice_number}.pdf`;
        a.click();
        URL.revokeObjectURL(blobUrl);
      })
      .catch(() => toast.error('Failed to download PDF'));
  };

  const handleSendEmail = async () => {
    setSendingEmail(true);
    try {
      await HttpService.post(API_ROUTES.invoiceSendEmail.replace(':id', id!), {});
      toast.success('Invoice sent via email!');
      setInvoice((prev) => (prev ? { ...prev, status: 'SENT' } : prev));
    } catch {
      toast.error('Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  const fmt = (n: string | number) =>
    `$${parseFloat(String(n)).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;

  const invoiceDate = invoice
    ? new Date(invoice.generated_at).toLocaleDateString('en-GB')
    : '';

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={8}>
        <CircularProgress />
      </Box>
    );
  }

  if (!invoice) {
    return (
      <Box p={3}>
        <Typography>Invoice not found.</Typography>
      </Box>
    );
  }

  return (
    <Box p={3}>
      {/* Toolbar */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => router.push(PAGE_ROUTES.INVOICE)}
        >
          Back
        </Button>
        <Box display="flex" gap={1} alignItems="center">
          <Chip
            label={invoice.status}
            size="small"
            sx={{
              bgcolor: invoice.status === 'SENT' ? '#d4edda' : '#fff3cd',
              color: invoice.status === 'SENT' ? '#155724' : '#856404',
            }}
          />
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadPdf}
          >
            Download PDF
          </Button>
          <Button
            variant="contained"
            startIcon={
              sendingEmail ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <SendIcon />
              )
            }
            onClick={handleSendEmail}
            disabled={sendingEmail}
            sx={{ bgcolor: OliveHeader, '&:hover': { bgcolor: '#5a4d0f' } }}
          >
            Send Email
          </Button>
        </Box>
      </Box>

      {/* Invoice Preview */}
      <Paper
        elevation={3}
        sx={{
          p: 5,
          maxWidth: 900,
          mx: 'auto',
          fontFamily: 'Arial, sans-serif',
        }}
      >
        {/* Title */}
        <Typography
          variant="h3"
          fontWeight="bold"
          color={OliveHeader}
          textAlign="right"
          mb={4}
        >
          INVOICE
        </Typography>

        {/* Header Table */}
        <Table size="small" sx={{ mb: 4 }}>
          <TableBody>
            <TableRow>
              {['BILL TO', 'INVOICE #', 'DATE'].map((h) => (
                <TableCell
                  key={h}
                  sx={{
                    bgcolor: OliveHeader,
                    color: '#fff',
                    fontWeight: 'bold',
                    width: '33%',
                  }}
                >
                  {h}
                </TableCell>
              ))}
            </TableRow>
            <TableRow>
              <TableCell>{invoice.client.company_name}</TableCell>
              <TableCell>
                <strong>{invoice.invoice_number}</strong>
              </TableCell>
              <TableCell>
                <strong>{invoiceDate}</strong>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>{invoice.client.address_line1}</TableCell>
              <TableCell
                sx={{ bgcolor: OliveHeader, color: '#fff', fontWeight: 'bold' }}
              >
                CUSTOMER ID
              </TableCell>
              <TableCell
                sx={{ bgcolor: OliveHeader, color: '#fff', fontWeight: 'bold' }}
              >
                TERMS
              </TableCell>
            </TableRow>
            {invoice.client.address_line2 ? (
              <>
                <TableRow>
                  <TableCell>{invoice.client.address_line2}</TableCell>
                  <TableCell>
                    <strong>{invoice.client.customer_id}</strong>
                  </TableCell>
                  <TableCell>
                    <strong>{invoice.client.terms}</strong>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>{invoice.client.city_state_zip}</TableCell>
                  <TableCell />
                  <TableCell />
                </TableRow>
              </>
            ) : (
              <TableRow>
                <TableCell>{invoice.client.city_state_zip}</TableCell>
                <TableCell>
                  <strong>{invoice.client.customer_id}</strong>
                </TableCell>
                <TableCell>
                  <strong>{invoice.client.terms}</strong>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        {/* Billing Period */}
        <Box mb={2}>
          <Box
            display="inline-block"
            bgcolor={OliveHeader}
            color="#fff"
            fontWeight="bold"
            px={2}
            py={0.5}
            fontSize={13}
            mb={0.5}
          >
            Billing Period
          </Box>
          <Typography fontWeight="bold">
            {invoice.billing_period_start} – {invoice.billing_period_end}
          </Typography>
        </Box>

        {/* Line Items */}
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: OliveHeader }}>
              {['Description', 'Team', 'Cost Per', 'Number of', ''].map((h) => (
                <TableCell key={h} sx={{ color: '#fff', fontWeight: 'bold' }}>
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {invoice.lineItems.map((item) => (
              <TableRow key={item.id} sx={{ borderBottom: '1px dashed #ccc' }}>
                <TableCell>{item.description}</TableCell>
                <TableCell>{item.team_label}</TableCell>
                <TableCell>$ {parseFloat(item.cost_per).toFixed(2)}</TableCell>
                <TableCell>{parseFloat(item.quantity).toFixed(2)}</TableCell>
                <TableCell align="right">
                  $ {parseFloat(item.amount).toFixed(2)}
                </TableCell>
              </TableRow>
            ))}
            <TableRow sx={{ bgcolor: LightYellow }}>
              <TableCell colSpan={3} />
              <TableCell>
                <strong>SUBTOTAL</strong>
              </TableCell>
              <TableCell align="right">
                <strong>{fmt(invoice.subtotal)}</strong>
              </TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: LightYellow }}>
              <TableCell colSpan={3} />
              <TableCell
                sx={{ color: OliveHeader, fontWeight: 'bold', fontSize: 16 }}
              >
                <strong>TOTAL</strong>
              </TableCell>
              <TableCell align="right" sx={{ fontSize: 16 }}>
                <strong>{fmt(invoice.total)}</strong>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>

        {/* Footer */}
        <Box textAlign="center" mt={5}>
          <Typography fontStyle="italic" fontWeight="bold" color={OliveHeader}>
            Thank you for your business!
          </Typography>
          <Typography fontSize={12} color="#666">
            If you have any questions about this invoice, please contact us.
          </Typography>
        </Box>
      </Paper>

      {/* Game Records Accordion */}
      <Box mt={3} maxWidth={900} mx="auto">
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight="bold">
              View Billed Games ({invoice.gameRecords.length} games)
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box display="flex" flexWrap="wrap" gap={1}>
              {invoice.gameRecords.map((g) => (
                <Chip
                  key={g.game_id}
                  label={g.game_id}
                  size="small"
                  variant="outlined"
                />
              ))}
            </Box>
          </AccordionDetails>
        </Accordion>
      </Box>
    </Box>
  );
};

export default InvoiceDetail;
